import { supabase } from "@/integrations/supabase/client";
import defaultLogo from "@/assets/csv-pro-logo-new.png";

/**
 * Hierarchical logo resolution:
 *   1. Portfolio Company (portfolios) logo
 *   2. Sub-Organization logo
 *   3. Client logo
 *   4. Default platform logo
 *
 * logo_url values are relative storage paths (e.g. "client/world-bank-group/logo-273c505f.png").
 * Pass the raw DB values; this helper resolves them to full public URLs via Supabase SDK.
 */

export interface BrandingLogos {
  companyLogoUrl?: string | null;   // portfolios.logo_url
  portfolioLogoUrl?: string | null; // sub_organizations.logo_url
  clientLogoUrl?: string | null;    // clients.logo_url
}

/**
 * Convert a relative storage path to a full public URL using Supabase SDK.
 * Never manually construct URLs.
 */
export function getLogoPublicUrl(relativePath: string): string {
  if (!relativePath) return "";
  // If it's already a full URL, return as-is
  if (relativePath.startsWith("http")) return relativePath;
  // Strip leading "org-assets/" if accidentally stored with bucket prefix
  const cleanPath = relativePath.replace(/^org-assets\//, "");
  const { data } = supabase.storage.from("org-assets").getPublicUrl(cleanPath);
  return data.publicUrl;
}

/**
 * Returns the best available logo URL using hierarchical fallback.
 * Priority: company → sub-org → client → default platform logo
 */
export function resolveLogo(logos: BrandingLogos): string {
  const raw =
    logos.companyLogoUrl ||
    logos.portfolioLogoUrl ||
    logos.clientLogoUrl;

  if (!raw) return defaultLogo;
  return getLogoPublicUrl(raw);
}

export { defaultLogo };
